import React, { useEffect, useRef } from "react";
import xstream from "state-x-stream";
import PropTypes from "prop-types";
import { brushY, select } from "d3";
import {
  auditTime,
  debounceTime,
  distinctUntilChanged,
  map,
  Subject,
  tap,
  takeUntil,
  of,
  catchError,
} from "rxjs";
import clamp from "../../../../common/helpers/clamp";

const errorHandler = (e) => {
  console.error(e);
  return of(null);
};

function controller({ props$ }) {
  const { zoomService } = props$.value;
  const destroy$ = new Subject();

  const brushBehavior = brushY()
    .on("start", ({ sourceEvent: e }) => e && e.stopPropagation())
    .on("brush", onBrush);

  function onBrush({ selection, sourceEvent }) {
    if (sourceEvent) {
      const currentZoom = zoomService.zoomId$.value;
      const { scale } = zoomService.yScale$.value;
      const y = -scale.invert(selection[0]);
      currentZoom.y = y;
      zoomService.setZoomId(currentZoom);
    }
  }

  function getSelection() {
    const zoomId = zoomService.zoomId$.value;
    const scale = zoomService.yScale$.value;
    const start = scale.scale(-zoomId.y);
    const height = scale.scale(scale.r2);
    const end = start + height;

    if (end >= scale.r2) {
      const { r2, d2 } = scale;
      const diff = r2 - d2;
      const maxY = diff < 0 ? diff : 0;
      const y = clamp(zoomId.y, maxY, 0);
      if (y !== zoomId.y) {
        zoomId.y = y;
        zoomService.setZoomId(zoomId);
      }
    }
    return [start, end];
  }

  function isScrollable(height) {
    const { rangeDist } = zoomService.yScale$.value;
    return rangeDist > height;
  }

  function updateExtent(el) {
    const { r2 } = zoomService.yScale$.value;
    const p1 = [0, 0];
    const p2 = [100, r2];
    el.call(brushBehavior.extent([p1, p2]));
  }

  function moveToSelection(el, selection) {
    el.call(brushBehavior.move, selection);
  }

  function compareEpsilon(e) {
    return (a, b) => {
      if (!a) return false;
      if (!b) return false;
      const p1Diff = Math.abs(a[0] - b[0]);
      const p2Diff = Math.abs(a[1] - b[1]);
      return p1Diff < e && p2Diff < e;
    };
  }

  function updateEls({ brushEl, scrollHandleEl, containerEl }) {
    const brush = select(brushEl);
    const isPanning$ = new Subject();
    const scrollBar = select(scrollHandleEl);
    const container = select(containerEl);

    isPanning$
      .pipe(
        distinctUntilChanged(),
        tap((x) => container.classed("panning", x)),
        takeUntil(destroy$),
        catchError(errorHandler)
      )
      .subscribe();

    zoomService.yScale$
      .pipe(
        debounceTime(100),
        map(() => getSelection()),
        distinctUntilChanged(compareEpsilon(0.5)),
        tap((sel) => scrollBar.attr("height", Math.max(5, sel[1] - sel[0]))),
        tap(() => updateExtent(brush)),
        tap((sel) => moveToSelection(brush, sel)),
        map((sel) => isScrollable(sel[1] - sel[0])),
        tap((isScroll) => container.classed("tw-hidden", !isScroll)),
        takeUntil(destroy$),
        catchError(errorHandler)
      )
      .subscribe();

    zoomService.scroll$
      .pipe(
        tap(() => isPanning$.next(true)),
        map(() => getSelection()),
        distinctUntilChanged(compareEpsilon(2)),
        tap((sel) => scrollBar.style("transform", `translateY(${sel[0]}px)`)),
        auditTime(200),
        tap(() => updateExtent(brush)),
        tap((sel) => moveToSelection(brush, sel)),
        debounceTime(800),
        tap(() => isPanning$.next(false)),
        takeUntil(destroy$),
        catchError(errorHandler)
      )
      .subscribe();
  }

  return {
    destroyEls: () => destroy$.next(),
    updateEls,
  };
}

function Scrollbar({ updateEls, destroyEls }) {
  const brushRef = useRef(null);
  const scrollHandleRef = useRef(null);
  const containerRef = useRef(null);

  useEffect(() => {
    const brushEl = brushRef.current;
    const scrollHandleEl = scrollHandleRef.current;
    const containerEl = containerRef.current;
    updateEls({ brushEl, scrollHandleEl, containerEl });
    return () => destroyEls();
  }, [updateEls, destroyEls]);

  return (
    <div className="scrollbar" ref={containerRef}>
      <svg height="100%" width="20">
        <rect
          ref={scrollHandleRef}
          x="2"
          width="10"
          fill="black"
          height="50"
          rx="5"
          ry="5"
          y={1}
          className="tw-fill-current tw-text-blueGray-600"
        />
        <g
          ref={brushRef}
          width="20"
          height="100%"
          className="brush tw-opacity-0"
        />
      </svg>
    </div>
  );
}

Scrollbar.propTypes = {
  updateEls: PropTypes.func.isRequired,
  destroyEls: PropTypes.func.isRequired,
};

export default xstream(controller)(Scrollbar);
